/**
 * "Which agent?": one setup snippet at a time for Claude Code, Codex, VS Code,
 * Cursor or any other MCP client, with the server address always shown.
 * Public (the sign-in screen and /docs/ai-setup), so it holds nothing secret:
 * the sign-in forms carry no credential, and Cursor's reads an API key from
 * the environment. Snippets come from handoff.ts.
 */
import { useId, useState } from 'react';
import { CodeBlock, CopyButton, Tabs } from '../ui/kit';
import { TOKEN_ENV_VAR, claudeCodeOAuthCommand, codexCommand, cursorMcpJson, mcpUrl, vsCodeCommand } from './handoff';

export type AgentChoice = 'claude' | 'codex' | 'vscode' | 'cursor' | 'other';

export function AgentPicker() {
  const [agent, setAgent] = useState<AgentChoice>('claude');
  const addressId = useId();
  const address = mcpUrl();
  return (
    <div className="stack-sm agent-picker">
      <Tabs
        label="Your agent"
        value={agent}
        onChange={setAgent}
        tabs={[
          {
            value: 'claude',
            label: 'Claude Code',
            panel: <CodeBlock caption="Terminal, at your website repo’s root" code={claudeCodeOAuthCommand()} />,
          },
          {
            value: 'codex',
            label: 'Codex',
            panel: <CodeBlock caption="Terminal, at your website repo’s root" code={codexCommand()} />,
          },
          {
            value: 'vscode',
            label: 'VS Code',
            panel: (
              <div className="stack-sm">
                <CodeBlock caption="Terminal" code={vsCodeCommand()} />
                <p className="small muted">Then ask Copilot in agent mode to set up FinderlyFix; VS Code asks you to sign in first.</p>
              </div>
            ),
          },
          {
            value: 'cursor',
            label: 'Cursor',
            panel: (
              <div className="stack-sm">
                <CodeBlock caption=".cursor/mcp.json" code={cursorMcpJson()} />
                <p className="small muted">
                  Cursor can’t sign in through the browser here yet: create an API key on AI agent access and set{' '}
                  <code>{TOKEN_ENV_VAR}</code> in your shell.
                </p>
              </div>
            ),
          },
          {
            value: 'other',
            label: 'Other',
            panel: (
              <p className="small muted">
                Claude on the web or desktop: Customize → Connectors → Add custom connector, and paste the address below. Any other MCP
                client: add the address; if it can’t sign in through the browser, send an API key as <code>Authorization: Bearer</code>.
              </p>
            ),
          },
        ]}
      />
      <div className="keyline">
        <span className="small muted" id={addressId}>
          MCP server
        </span>
        <code className="key">{address}</code>
        <CopyButton text={address} label="Copy" className="btn btn-sm" describedBy={addressId} />
      </div>
    </div>
  );
}
